import { MButton, MDialog, MIcons, MSpinner, MTextField } from "@material-dev/insights-portal-ui-components";
import { LoadingState } from "@material-dev/insights-portal-ui-components/lib/types";
import debounce from "lodash.debounce";
import React, { useMemo, FC, useCallback, useState, useEffect, CSSProperties, useRef } from "react";
import { batch } from "react-redux";
import { FixedSizeList } from "react-window";
import { useAppDispatch, useAppSelector } from "@store";
import { SortDirections } from "app/ApiClient";
import { COMMON_DATETIME_FORMAT } from "const/dateFormats";
import {
  getAllProjects,
  getProjectSelectorModifications,
  getProjectSelectorVersions,
  resetProjectSelectorModifications,
  resetProjectSelectorVersions,
} from "slices/projectSelector/projectSelectorSlice";
import { NormalizedShortProject } from "slices/projectSelector/projectSelectorState";
import { getNormalizedOriginSource } from "utils/projectSourceHelper";
import { useProjectSelectorStyles } from "./ProjectSelectorDialogStyles";
import { useNormalizedModifications } from "./useNormalizedModifications";
import { useNormalizedProjects } from "./useNormalizedProjects";
import { useNormalizedVersions } from "./useNormalizedVersions";

export type ExtendNormalizedShortProject = NormalizedShortProject & { versionId: string };
const DEBOUNCED_DELAY = 300;
const LIST_HEIGHT = 500;
const LIST_ITEM_HEIGHT = 50;
export type ModificationItem = NormalizedShortProject & { versionId: string; modificationId: number };
type ProjectSelectorDialogProps = {
  projectId?: string;
  onClose: () => void;
  onBack?: () => void;
  isModificationSelector?: boolean;
  versionId?: string;
  modificationId?: number;
  isOpen: boolean;
  onVersionSelect?: (item: ExtendNormalizedShortProject) => void;
  onModificationSelect?: (item: ModificationItem) => void;
};

const ProjectSelectorDialog: FC<ProjectSelectorDialogProps> = ({
  onClose,
  onVersionSelect,
  projectId,
  versionId,
  isOpen,
  onBack,
  onModificationSelect,
  isModificationSelector,
}) => {
  const { classes, cx } = useProjectSelectorStyles();
  const [selectedProject, setSelectedProject] = useState<NormalizedShortProject | undefined>();
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>();
  const [selectedModification, setSelectedModification] = useState<number | undefined>();
  const projectsListRef = useRef<FixedSizeList | null>(null);
  const [projectSorting, setProjectSorting] = useState(SortDirections.Asceding);
  const [modificationSorting, setModificationSorting] = useState(SortDirections.Asceding);

  const [searchString, setSearchString] = useState<string>("");
  const dispatch = useAppDispatch();
  const { projects } = useNormalizedProjects(projectSorting, searchString);
  const { sortedModifications } = useNormalizedModifications(modificationSorting);
  const { versionsLoading, modificationsLoading } = useAppSelector((state) => state.projectSelector);
  const [versionSorting, setVersionSorting] = useState(SortDirections.Desceding);
  const { sortedVersions, maxVersionId } = useNormalizedVersions(versionSorting);
  // flag to catch that list reference is assigned
  const [listRendered, setListRendered] = useState(false);

  const handleClose = useCallback(() => {
    batch(() => {
      dispatch(resetProjectSelectorVersions());
      dispatch(resetProjectSelectorModifications());
    });
    setListRendered(false);
    setSearchString("");
    setVersionSorting(SortDirections.Desceding);
    setSelectedVersion(undefined);
    setSelectedProject(undefined);
    setSelectedModification(undefined);
    setProjectSorting(SortDirections.Asceding);
    setModificationSorting(SortDirections.Asceding);
  }, []);
  useEffect(() => {
    //reset on Close
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen, handleClose]);

  useEffect(() => {
    setSelectedVersion(versionId);
  }, [versionId]);

  useEffect(() => {
    if (isOpen && projects.length > 0 && projectId && projectsListRef.current) {
      // scroll to target project if projectId passed
      const targetIndex = projects.findIndex((pr) => pr.originProjectId === projectId);
      projectsListRef.current.scrollToItem(targetIndex, "smart");
    }
  }, [listRendered, projects, projectId, isOpen]);
  useEffect(() => {
    if (isOpen && projects.length > 0 && projectId) {
      // select target project if projectId passed
      const targetProject = projects.find((pr) => pr.originProjectId === projectId);
      setSelectedProject(targetProject);
    }
  }, [projects, projectId, isOpen]);

  useEffect(() => {
    dispatch(getAllProjects());
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(() => {
    if (isModificationSelector && onModificationSelect && selectedProject && selectedVersion && selectedModification) {
      onModificationSelect({ ...selectedProject, versionId: selectedVersion, modificationId: selectedModification });
      return;
    }
    if (selectedProject && selectedVersion && onVersionSelect && !isModificationSelector) {
      onVersionSelect({ ...selectedProject, versionId: selectedVersion });
    }
  }, [
    onVersionSelect,
    selectedModification,
    selectedProject,
    selectedVersion,
    onModificationSelect,
    isModificationSelector,
  ]);

  const submitDisabled = !selectedProject || !selectedVersion || (isModificationSelector && !selectedModification);
  const actions = useMemo(() => {
    return (
      <div className={classes.actions}>
        <MButton variant="outlined" color="primary" onClick={handleCancel}>
          Cancel
        </MButton>
        <MButton variant="contained" disabled={submitDisabled} color="primary" onClick={handleSelect}>
          {isModificationSelector ? "Import" : "Select"}
        </MButton>
      </div>
    );
  }, [selectedProject, selectedVersion, handleSelect, handleCancel, submitDisabled, isModificationSelector]);

  useEffect(() => {
    if (selectedProject) {
      dispatch(
        getProjectSelectorVersions({
          projectId: selectedProject.originProjectId,
          source: getNormalizedOriginSource(selectedProject.id, selectedProject.originSource),
        }),
      );
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedVersion && selectedProject?.id && isModificationSelector) {
      dispatch(
        getProjectSelectorModifications({
          projectId: selectedProject?.id,
          versionId: selectedVersion,
        }),
      );
    }
  }, [selectedVersion, isModificationSelector, selectedProject]);

  const renderRow = useCallback(
    ({ index, style }: { index: number; style: CSSProperties | undefined }) => {
      if (projects.length > index) {
        const item = projects[index];
        const onProjectClick = (project: NormalizedShortProject) => () => {
          setSelectedProject(project);
          setSelectedVersion(undefined);
        };
        return (
          <div
            key={`${item.originProjectId}-${item.name}-${index}`}
            onClick={onProjectClick(item)}
            className={cx(
              classes.listItem,
              selectedProject?.originProjectId === item.originProjectId && classes.itemSelected,
            )}
            style={style}>
            {`${item.originProjectId} ${item.name}`}
          </div>
        );
      }
      return null;
    },
    [classes, projects, selectedProject],
  );
  const debounceSearch = useMemo(
    () =>
      debounce((text: string) => {
        setSearchString(text);
      }, DEBOUNCED_DELAY),
    [],
  );

  const handleVerionSelected = useCallback(
    (version: string) => () => {
      setSelectedVersion(version);
    },
    [],
  );

  const handleModificationSelect = useCallback(
    (modificationId: number) => () => {
      setSelectedModification(modificationId);
    },
    [],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => debounceSearch(e.target.value),
    [debounceSearch],
  );
  const handleSortProjects = () => {
    setProjectSorting(projectSorting === SortDirections.Asceding ? SortDirections.Desceding : SortDirections.Asceding);
  };
  const handleSortVersions = () => {
    setVersionSorting(versionSorting === SortDirections.Asceding ? SortDirections.Desceding : SortDirections.Asceding);
  };
  const handleSortModification = () => {
    setModificationSorting(
      modificationSorting === SortDirections.Asceding ? SortDirections.Desceding : SortDirections.Asceding,
    );
  };
  const handleListRendered = ({ overscanStartIndex }: { overscanStartIndex: number }) => {
    if (overscanStartIndex === 0) {
      setListRendered(true);
    }
  };
  return (
    <MDialog open={isOpen} onClose={handleCancel} actionsElement={actions} maxWidth="lg">
      {isModificationSelector && (
        <div className={classes.title}>
          <span onClick={onBack} className={classes.btnBack}>
            <MIcons.ArrowBack />
          </span>
          Select a modification to import
        </div>
      )}
      <div className={cx(classes.root, isModificationSelector && classes.modificationRoot)}>
        <div className={classes.projects}>
          <div className={classes.searchBar}>
            <MTextField
              className={classes.search}
              fullWidth
              placeholder="Search for dataset ID, dataset name"
              InputProps={{
                startAdornment: <MIcons.Search />,
              }}
              onChange={handleSearch}
            />
          </div>
          <div className={classes.list}>
            <div className={classes.sortHeader} onClick={handleSortProjects}>
              <span>Datasets</span>
              <MIcons.SortIcon className={classes.sortIcon} />
            </div>
            <FixedSizeList
              onItemsRendered={handleListRendered}
              ref={projectsListRef}
              height={LIST_HEIGHT}
              width={"100%"}
              itemCount={projects.length}
              itemSize={LIST_ITEM_HEIGHT}>
              {renderRow}
            </FixedSizeList>
          </div>
        </div>
        <div className={classes.versions}>
          <div className={cx(classes.list, classes.extraList)}>
            <div className={classes.sortHeader} onClick={handleSortVersions}>
              <span>Encoded Versions</span>
              <MIcons.SortIcon className={classes.sortIcon} />
            </div>
            <MSpinner className={classes.spinner} isLoading={versionsLoading === LoadingState.pending} />
            <div>
              {sortedVersions.map((item) => (
                <div
                  onClick={handleVerionSelected(item.versionId)}
                  key={`${item.versionId}-${item.versionDate}`}
                  className={cx(classes.listItem, selectedVersion === item.versionId && classes.itemSelected)}>
                  {item.versionDate.toDateFormat(COMMON_DATETIME_FORMAT)}
                  {maxVersionId === item.versionId && <div className={classes.latestLabel}>Latest</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
        {isModificationSelector && (
          <div className={classes.modifications}>
            <div className={cx(classes.list, classes.extraList)}>
              <div className={classes.sortHeader} onClick={handleSortModification}>
                <span>Modified files</span>
                <MIcons.SortIcon className={classes.sortIcon} />
              </div>
              <MSpinner className={classes.spinner} isLoading={modificationsLoading === LoadingState.pending} />
              <div>
                {sortedModifications.map((item) => (
                  <div
                    onClick={handleModificationSelect(item.id)}
                    key={`${item.id}`}
                    className={cx(classes.listItem, selectedModification === item.id && classes.itemSelected)}>
                    {item.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </MDialog>
  );
};
export default ProjectSelectorDialog;
